/**
 * Wat client en server elkaar sturen.
 *
 * Dit is het enige stuk dat allebei de kanten dwingend delen, en het wordt aan **allebei**
 * de kanten gevalideerd. Niet uit wantrouwen tegen de eigen app, maar omdat de server geen
 * enkele aanname mag doen over wie er belt: een verzoek dat niet door het schema komt wordt
 * geweigerd en gelogd, nooit "best effort" doorgelaten (plan §6).
 *
 * Elke fout draagt een **code**, geen zin. De zin is voor het scherm en mag veranderen; de
 * code is voor de app en mag dat niet. Zo kan de UI iets zinnigs doen — opnieuw proberen,
 * doorsturen naar het inlogscherm — in plaats van tekst te vergelijken.
 */
import { z } from 'zod';

/**
 * De vorm van een uitnodigingscode: zes tekens zonder de verwarrende.
 *
 * Geen 0/O en geen 1/I/L, want die haal je door de telefoon door elkaar. Dezelfde vorm als
 * de codes voor een potje, en dezelfde als de `CHECK` in de database.
 */
export const CODE_ALFABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CodeSchema = z.string().regex(/^[A-Z2-9]{6}$/, 'een code is zes tekens');

/**
 * Een gebruikersnaam.
 *
 * Hoofdletterongevoelig uniek — "Thijs" en "thijs" is dezelfde naam — en dat wordt in de
 * database afgedwongen met een index op `lower(username)`.
 */
export const GebruikersnaamSchema = z
  .string()
  .min(3, 'een naam is minstens drie tekens')
  .max(24, 'een naam is hoogstens vierentwintig tekens')
  .regex(/^[A-Za-z0-9_-]+$/, 'letters, cijfers, - en _');

/**
 * Een wachtwoord.
 *
 * Acht tekens minimaal en verder geen eisen aan hoofdletters of leestekens. Die eisen
 * leveren aantoonbaar zwákkere wachtwoorden op: mensen maken er `Wachtwoord1!` van. Lengte
 * is wat telt.
 *
 * Het waren er tien; op verzoek acht, omdat tien in de praktijk als hinderlijk voelde. Dat
 * is een bewuste ruil: acht tekens zonder verdere eisen is merkbaar zwakker. Wat die ruil
 * draagbaar maakt is de rest van de opzet — de app is besloten (alleen op uitnodiging),
 * inloggen is aan een teller gebonden, en wachtwoorden liggen achter scrypt. Raden op
 * schaal is dus duur, en er is geen open aanmeldformulier om op los te gaan.
 */
export const WachtwoordSchema = z
  .string()
  .min(8, 'een wachtwoord is minstens acht tekens')
  .max(200, 'dat wachtwoord is wel erg lang');

export const RegistrerenSchema = z
  .object({
    code: CodeSchema,
    gebruikersnaam: GebruikersnaamSchema,
    wachtwoord: WachtwoordSchema,
  })
  .strict();
export type Registreren = z.infer<typeof RegistrerenSchema>;

export const InloggenSchema = z
  .object({ gebruikersnaam: z.string().min(1), wachtwoord: z.string().min(1) })
  .strict();
export type Inloggen = z.infer<typeof InloggenSchema>;

/** Wie je bent, zoals de server je terugmeldt. */
export const IkSchema = z
  .object({
    id: z.string().uuid(),
    gebruikersnaam: GebruikersnaamSchema,
  })
  .strict();
export type Ik = z.infer<typeof IkSchema>;

/**
 * De foutcodes die de server teruggeeft.
 *
 * `ONBEKEND_OF_FOUT_WACHTWOORD` is met opzet één code voor twee gevallen: zou de server
 * onderscheid maken, dan kun je met een reeks inlogpogingen uitvinden wélke namen bestaan.
 */
export const FOUTCODES = [
  'ONGELDIG_VERZOEK',
  'CODE_ONGELDIG',
  'CODE_VERLOPEN',
  'CODE_GEBRUIKT',
  'NAAM_BEZET',
  'ONBEKEND_OF_FOUT_WACHTWOORD',
  'TE_VEEL_POGINGEN',
  'NIET_INGELOGD',
  'PROTOCOL_VERSCHIL',
  'SERVERFOUT',
  // En de fouten uit het spel zelf:
  'ILLEGAL_MOVE',
  'NIET_JOUW_POTJE',
  'NIET_JOUW_BEURT',
  'STALE_SEQ',
  'POTJE_ONBEKEND',
  'ANDER_IS_ER_NOG',
  'AL_AFGELOPEN',
  'ONHERSPEELBAAR',
] as const;
export type Foutcode = (typeof FOUTCODES)[number];

export const FoutSchema = z
  .object({
    /**
     * Elke fout draagt `type: 'fout'`, net als de spelfouten.
     *
     * Dat leek eerst overbodig — de statuscode zegt toch al dat het mis is? Maar de
     * client leest antwoorden met één schema, een discriminated union op `type`. Zonder
     * dit veld viel élke accountfout daar doorheen, en zag de speler "de server gaf 401"
     * in plaats van "je sessie is verlopen".
     */
    type: z.literal('fout'),
    fout: z.enum(FOUTCODES),
    /** Een zin voor het scherm. Mag veranderen; de code niet. */
    bericht: z.string(),
    /** Bij `TE_VEEL_POGINGEN`: hoeveel seconden je moet wachten. */
    wachtSeconden: z.number().int().nonnegative().optional(),
  })
  .strict();
export type Fout = z.infer<typeof FoutSchema>;

/** Het antwoord op registreren en inloggen: dan ben je binnen. */
export const SessieSchema = z.object({ ik: IkSchema }).strict();
export type Sessie = z.infer<typeof SessieSchema>;

/**
 * Het antwoord op "wie ben ik".
 *
 * Met `ik: null` als niemand er is, en met status 200. Niet 401: "wie ben ik" is geen
 * afgeschermde bron maar een vraag naar de stand van zaken, en "niemand" is daarop een
 * volwaardig antwoord. Gaf hij 401, dan zou elke bezoeker zonder account bij elke
 * laadbeurt een fout in zijn console zien — en dan leer je die console te negeren, wat
 * precies is wat je niet wilt.
 */
export const WieBenIkSchema = z.object({ ik: IkSchema.nullable() }).strict();
export type WieBenIk = z.infer<typeof WieBenIkSchema>;

/** Hoe de sessiecookie heet. Eén naam, aan beide kanten. */
export const SESSIE_COOKIE = 'catan_sessie';

// ---------------------------------------------------------------- een potje

export const MOEILIJKHEDEN = ['Beginner', 'Gevorderd', 'Meester'] as const;
export const MoeilijkheidSchema = z.enum(MOEILIJKHEDEN);

/** Wat je meestuurt om een oefenpotje te beginnen. */
export const NieuwOefenpotjeSchema = z
  .object({
    tegenstander: z.string().min(1),
    figuur: z.string().min(1),
    moeilijkheid: MoeilijkheidSchema,
    /** Verdeel je je startlandschappen zelf? */
    zelfVerdelen: z.boolean(),
    /**
     * Het lot vastleggen — voor toetsen die een bepaalde situatie willen bereiken.
     *
     * Onschuldig: de seed bepaalt alleen welk potje je krijgt, niet wat je erin mag. Alle
     * regels blijven gelden en de tegenstander speelt gewoon. Streng begrensd omdat hij
     * ongefilterd in de database belandt.
     */
    seed: z.string().regex(/^[a-z0-9-]{1,48}$/i).optional(),
  })
  .strict();
export type NieuwOefenpotje = z.infer<typeof NieuwOefenpotjeSchema>;

/**
 * Een zet.
 *
 * De actie zelf blijft hier `unknown`: de vorm ervan staat in `@catan/rules`, en die
 * hoort niet in het protocol te worden overgetypt — dat zou twee waarheden geven die
 * uiteen kunnen lopen. De server toetst hem alsnog, want `isLegalAction` weigert alles
 * wat geen geldige zet is.
 */
export const ZetSchema = z
  .object({
    type: z.literal('zet'),
    seq: z.number().int().nonnegative(),
    /**
     * Alleen hexadecimale tekens — dat is wat `crypto.randomUUID()` zonder streepjes
     * oplevert, en het is met opzet nauwer dan "elke string": de server gebruikt zijn
     * eigen sleutels (met een dubbele punt erin) voor zetten die hij zélf doet, zoals het
     * afwijzen van een verlopen ruilaanbod. Kon de client die vorm ook sturen, dan kon
     * hij die sleutels vooraf bezetten en daarmee het potje voor beide spelers slopen.
     */
    idempotencyKey: z.string().regex(/^[0-9a-f]{16,64}$/, 'geen geldige zetsleutel'),
    action: z.unknown(),
  })
  .strict();

// (Er was hier ooit een HervatSchema voor "stuur me wat ik gemist heb". Herverbinden
// haalt in de praktijk gewoon de hele stand op — dat is bij dit spel klein genoeg — en
// een schema dat nergens aan vastzit is een belofte die stilletjes kan gaan liegen.)

/**
 * Eén gespeelde zet, zoals de tegenstander hem mag zien.
 *
 * Hiermee kan het scherm de beurt van de ander afspelen in plaats van de stand te laten
 * verspringen. `actie` blijft `unknown` om dezelfde reden als bij `ZetSchema`: de vorm
 * hoort in `@catan/rules` en niet hier overgetypt, want dat geeft twee waarheden die uiteen
 * kunnen lopen. De server saneert hem met `zichtbareActie` vóór hij de deur uit gaat.
 */
export const ZichtbareZetSchema = z
  .object({
    seq: z.number().int().nonnegative(),
    seat: z.number().int().min(0).max(1),
    actie: z.unknown(),
    /** De regels van precies deze zet, oudste eerst. */
    log: z.array(z.string()),
  })
  .strict();
export type ZichtbareZet = z.infer<typeof ZichtbareZetSchema>;

/**
 * Wat de server terugstuurt.
 *
 * `weergave` draagt de hele weergave, geen verschil. Dat is met opzet: een verschil moet je
 * aan beide kanten goed toepassen, en één misgelopen bericht laat het scherm dan iets
 * anders zien dan de server bedoelt. De weergave is klein genoeg.
 *
 * **Niet `.strict()`, en dat is een keuze.** Streng hoort bij wat de server *aanneemt*
 * (`ZetSchema`, `RegistrerenSchema` — onvertrouwde invoer). Wat de client *ontvangt* hoort
 * tolerant te zijn: de straf op een onbekend veld is daar geen rode toets bij ons maar een
 * bevroren scherm bij de speler. Dat gebeurde ook, en het staat hieronder bij `figuur`
 * beschreven. Een tabblad dat een uitrol overleeft en een veld ziet dat het niet kent, moet
 * gewoon doorspelen.
 */
export const WeergaveSchema = z
  .object({
    type: z.literal('weergave'),
    seq: z.number().int().nonnegative(),
    /** De regels die er sinds de vorige weergave bij kwamen, oudste eerst. */
    log: z.array(z.string()),
    view: z.unknown(),
    /**
     * Welk figuur jij in dit potje speelt.
     *
     * Alleen bij het ophalen van een potje; na een zet verandert hij niet. Hij hoort erbij
     * omdat je bij het hervatten je eigen portret terug moet krijgen, en dat weet de
     * browser niet meer als je net een nieuw tabblad hebt geopend.
     *
     * Dit schema was `.strict()`, en toen liet een veld dat er niet in staat het hele
     * bericht afketsen. Dat gebeurde ook: de client meldde "de server gaf 200" terwijl de
     * server keurig antwoordde — het was het schema dat nee zei. Zie de toelichting boven
     * het schema waarom het nu tolerant is.
     */
    figuur: z.string().nullable().optional(),
    /**
     * Het figuur van je tegenstander, zodat hij niet als Louis in beeld komt.
     *
     * Alleen aanwezig als de client hem vroeg met `?sinds=`. Dat is het opt-in-teken: een
     * oude bundel stuurt die parameter nooit en krijgt letterlijk het antwoord van vroeger.
     */
    tegenfiguur: z.string().nullable().optional(),
    /**
     * De zetten sinds `sinds`, oudste eerst — zodat het scherm de beurt van de ander kan
     * afspelen.
     *
     * Optioneel, en om dezelfde reden als `tegenfiguur`: de client vraagt ze aan. De client
     * mag ze dus nooit als aanwezig aannemen; zonder `zetten` is er niets af te spelen en
     * gedraagt hij zich als voorheen.
     */
    zetten: z.array(ZichtbareZetSchema).optional(),
    /** Op welke stoel jij zit. Bij twee mensen is dat niet altijd stoel 0. */
    jouwStoel: z.number().int().min(0).max(1).optional(),
    /** Zit er een computer tegenover je? Dan hoeft het scherm niet te wachten op de ander. */
    tegenComputer: z.boolean().optional(),
    /**
     * Is je tegenstander al minuten niet meer gezien?
     *
     * Dan mag je het potje opgeven. Zonder dit blijft een partij waarin de ander is
     * weggelopen voor altijd op zijn beurt staan, en dat is geen einde maar een gijzeling.
     */
    anderWeg: z.boolean().optional(),
    /** `active`, `finished` of `abandoned`. */
    status: z.string().optional(),
  });

/**
 * Eén foutvorm voor het hele spel, spelfouten en accountfouten samen.
 *
 * Dit waren twee schema's met elk hun eigen codes, en dat betekende dat de helft van de
 * serverantwoorden niet door het clientschema kwam. Nu is elke fout `{ type: 'fout',
 * fout, bericht }`, wat er ook misging.
 */
export const SpelfoutSchema = FoutSchema;

export const NaarClientSchema = z.discriminatedUnion('type', [WeergaveSchema, FoutSchema]);
export type NaarClient = z.infer<typeof NaarClientSchema>;
